import { useState, useEffect, useRef, useCallback } from 'react'
import { supabase } from './supabase'
import { useToast } from './ToastContext'
import { formatDistanceToNow } from 'date-fns'
import { Send, AtSign } from 'lucide-react'
import { isActiveProfile } from './constants'

export default function TaskComments({ taskId, taskTitle, profiles, currentUser, assignedBy, assignedTo }) {
  const toast = useToast()
  const [comments, setComments] = useState([])
  const [text, setText] = useState('')
  const [saving, setSaving] = useState(false)
  const [showMentions, setShowMentions] = useState(false)
  const [mentionSearch, setMentionSearch] = useState('')
  const [cursorPos, setCursorPos] = useState(0)
  const textareaRef = useRef(null)
  const bottomRef = useRef(null)

  const loadComments = useCallback(async () => {
    const { data } = await supabase
      .from('task_comments')
      .select('*, author:profiles!task_comments_author_id_fkey(id, full_name, role)')
      .eq('task_id', taskId)
      .order('created_at', { ascending: true })
    setComments(data || [])
  }, [taskId])

  useEffect(() => { loadComments() }, [loadComments])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [comments])

  // Real-time subscription
  useEffect(() => {
    const channel = supabase
      .channel(`task-comments-${taskId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'task_comments',
        filter: `task_id=eq.${taskId}`,
      }, () => loadComments())
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, [taskId, loadComments])

  const handleTextChange = (e) => {
    const val = e.target.value
    setText(val)
    const pos = e.target.selectionStart
    setCursorPos(pos)

    // Detect @ mention
    const beforeCursor = val.slice(0, pos)
    const atMatch = beforeCursor.match(/@(\w*)$/)
    if (atMatch) {
      setMentionSearch(atMatch[1].toLowerCase())
      setShowMentions(true)
    } else {
      setShowMentions(false)
    }
  }

  const insertMention = (profile) => {
    const beforeCursor = text.slice(0, cursorPos)
    const afterCursor = text.slice(cursorPos)
    const atIndex = beforeCursor.lastIndexOf('@')
    const newText = beforeCursor.slice(0, atIndex) + `@${profile.full_name} ` + afterCursor
    setText(newText)
    setShowMentions(false)
    textareaRef.current?.focus()
  }

  const filteredProfiles = profiles.filter(p =>
    isActiveProfile(p) &&
    p.id !== currentUser.id &&
    p.full_name?.toLowerCase().includes(mentionSearch)
  )

  const extractMentions = (content) => {
    const mentions = []
    profiles.forEach(p => {
      if (content.includes(`@${p.full_name}`)) mentions.push(p.id)
    })
    return mentions
  }

  const submit = async () => {
    if (!text.trim()) return
    setSaving(true)
    try {
      const mentions = extractMentions(text)
      const { data: comment, error } = await supabase
        .from('task_comments')
        .insert({
          task_id: taskId,
          author_id: currentUser.id,
          content: text.trim(),
          mentions,
        })
        .select()
        .single()

      if (error) throw error

      // Notify mentioned users
      for (const mentionedId of mentions) {
        if (mentionedId !== currentUser.id) {
          await supabase.from('notifications').insert({
            user_id: mentionedId,
            task_id: taskId,
            type: 'mention',
            message: `You were mentioned in a comment on "${taskTitle}"`,
          })
        }
      }

      // Notify task assignee if they weren't mentioned (and aren't the commenter)
      if (assignedTo && assignedTo !== currentUser.id && !mentions.includes(assignedTo)) {
        await supabase.from('notifications').insert({
          user_id: assignedTo,
          task_id: taskId,
          type: 'comment',
          message: `New comment on "${taskTitle}"`,
        })
      }

      // Notify assigner if they weren't mentioned (and aren't the commenter)
      if (assignedBy && assignedBy !== currentUser.id && !mentions.includes(assignedBy) && assignedBy !== assignedTo) {
        await supabase.from('notifications').insert({
          user_id: assignedBy,
          task_id: taskId,
          type: 'comment',
          message: `New comment on "${taskTitle}"`,
        })
      }

      setText('')
      loadComments()
    } catch (e) {
      toast('Error posting comment: ' + e.message, 'error')
    } finally {
      setSaving(false)
    }
  }

  const renderContent = (content) => {
    // Highlight @mentions
    const parts = content.split(/(@\w[\w\s]*\w|\w+)/g)
    return content.split(' ').map((word, i) => {
      if (word.startsWith('@')) {
        const name = word.slice(1)
        const mentioned = profiles.find(p => p.full_name === name || p.full_name?.startsWith(name))
        if (mentioned) return (
          <span key={i} style={{ background: '#e8f0fe', color: '#1a56db', borderRadius: 4, padding: '1px 4px', fontWeight: 500 }}>
            {word}
          </span>
        )
      }
      return <span key={i}>{word} </span>
    })
  }

  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?'
  const colors = ['#2e3a59', '#2e7d6e', '#c4962a', '#9c5fa3', '#e8724a', '#3d7fc4', '#c94f7c']
  const getColor = (id) => colors[profiles.findIndex(p => p.id === id) % colors.length] || '#2e3a59'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Comments list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 0' }}>
        {comments.length === 0 && (
          <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: '24px 0', fontStyle: 'italic' }}>
            No comments yet — start the conversation
          </div>
        )}
        {comments.map(c => {
          const isMe = c.author_id === currentUser.id
          const author = profiles.find(p => p.id === c.author_id)
          return (
            <div key={c.id} style={{ display: 'flex', gap: 10, marginBottom: 16, padding: '0 20px' }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%', background: getColor(c.author_id),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 600, color: '#fff', flexShrink: 0
              }}>
                {getInitials(author?.full_name || c.author?.full_name)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)' }}>
                    {isMe ? 'You' : (author?.full_name || c.author?.full_name || 'Unknown')}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {formatDistanceToNow(new Date(c.created_at), { addSuffix: true })}
                  </span>
                </div>
                <div style={{
                  background: isMe ? 'var(--navy-mid)' : 'var(--cream)',
                  color: isMe ? '#fff' : 'var(--text-primary)',
                  borderRadius: isMe ? '12px 12px 4px 12px' : '4px 12px 12px 12px',
                  padding: '8px 12px', fontSize: 13.5, lineHeight: 1.5,
                  display: 'inline-block', maxWidth: '100%', wordBreak: 'break-word'
                }}>
                  {renderContent(c.content)}
                </div>
              </div>
            </div>
          )
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{ borderTop: '1px solid var(--border-light)', padding: '12px 16px', position: 'relative' }}>
        {/* Mention dropdown */}
        {showMentions && filteredProfiles.length > 0 && (
          <div style={{
            position: 'absolute', bottom: '100%', left: 16, right: 16,
            background: '#fff', border: '1px solid var(--border)', borderRadius: 8,
            boxShadow: 'var(--shadow-md)', overflow: 'hidden', marginBottom: 4
          }}>
            {filteredProfiles.map(p => (
              <div key={p.id}
                onClick={() => insertMention(p)}
                style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--cream)'}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                <div style={{ width: 24, height: 24, borderRadius: '50%', background: getColor(p.id), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600, color: '#fff' }}>
                  {getInitials(p.full_name)}
                </div>
                <span style={{ fontWeight: 500 }}>{p.full_name}</span>
                <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{p.role?.replace('_', ' ')}</span>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <textarea
              ref={textareaRef}
              value={text}
              onChange={handleTextChange}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit() }
                if (e.key === 'Escape') setShowMentions(false)
              }}
              placeholder="Add a comment... (@ to mention someone)"
              rows={2}
              style={{
                width: '100%', padding: '8px 12px', border: '1px solid var(--border)',
                borderRadius: 8, fontFamily: 'var(--font-body)', fontSize: 13.5,
                resize: 'none', outline: 'none', lineHeight: 1.5,
                transition: 'border-color 0.15s'
              }}
              onFocus={e => e.target.style.borderColor = 'var(--navy-mid)'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'}
            />
          </div>
          <button
            onClick={submit}
            disabled={saving || !text.trim()}
            style={{
              width: 36, height: 36, borderRadius: '50%', border: 'none',
              background: text.trim() ? 'var(--navy-mid)' : 'var(--border)',
              color: '#fff', cursor: text.trim() ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.15s', flexShrink: 0
            }}>
            <Send size={14} />
          </button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
          Enter to send · Shift+Enter for new line · @ to mention
        </div>
      </div>
    </div>
  )
}
